import React, { useEffect } from "react";
import {
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import appInsights from "../../AppInsights";

function WebsiteHits({ data }) {
  let sum = 0;
  data?.forEach((item) => {
    sum += item.value;
  });

  // Track component load
  useEffect(() => {
    const openTime = new Date().toLocaleString();
    appInsights.trackEvent({
      name: "WebsiteHits_Component_Loaded",
      properties: {
        openedAt: openTime,
        component: "Website_Visitors_Chart",
        totalVisits: sum,
        dataPoints: data?.length || 0,
        hasData: data && data.length > 0,
      },
    });
  }, []);

  // Track data changes and chart rendering
  useEffect(() => {
    if (data && data.length > 0) {
      appInsights.trackEvent({
        name: "WebsiteHits_Chart_Rendered",
        properties: {
          totalVisits: sum,
          dataPoints: data.length,
          chartType: "bar",
          weekDays: data.map(item => item.name),
          maxDailyVisits: Math.max(...data.map(item => item.value)),
          minDailyVisits: Math.min(...data.map(item => item.value)),
          averageDailyVisits: (sum / data.length).toFixed(1),
        },
      });
    }
  }, [data, sum]);

  // Track chart interactions - FIXED VERSION
  const handleBarInteraction = (data, index, event) => {
    try {
      // Add null checks for all accessed properties
      if (data?.activeLabel && data?.activePayload?.[0]?.payload) {
        const interactedData = data.activePayload[0].payload;
        const interactionType = event?.type || 'unknown';
        
        appInsights.trackEvent({
          name: "WebsiteHits_Bar_Interacted",
          properties: {
            day: data.activeLabel,
            visits: interactedData.value,
            dayPercentage: ((interactedData.value / sum) * 100).toFixed(1) + '%',
            interactionType: interactionType,
            totalWeeklyVisits: sum,
            positionInWeek: (data.activeTooltipIndex ?? index) + 1,
          },
        });
      }
    } catch (error) {
      console.error('Error in handleBarInteraction:', error);
      // Optionally track the error
      appInsights.trackException({
        exception: error,
        severityLevel: 2,
        properties: {
          name: "WebsiteHits_Interaction_Error",
          component: "handleBarInteraction",
        },
      });
    }
  };

  // Track chart hover events - FIXED VERSION
  const handleBarHover = (data, index, event) => {
    try {
      // Add null checks for all accessed properties
      if (data?.activeLabel && data?.activePayload?.[0]?.payload) {
        const hoveredData = data.activePayload[0].payload;
        
        // Safely calculate max and min values
        const values = data.map(item => item.value).filter(val => !isNaN(val));
        const maxValue = values.length > 0 ? Math.max(...values) : 0;
        const minValue = values.length > 0 ? Math.min(...values) : 0;

        appInsights.trackEvent({
          name: "WebsiteHits_Bar_Hovered",
          properties: {
            day: data.activeLabel,
            visits: hoveredData.value,
            dayOfWeek: hoveredData.name,
            isPeakDay: hoveredData.value === maxValue,
            isLowestDay: hoveredData.value === minValue,
          },
        });
      }
    } catch (error) {
      console.error('Error in handleBarHover:', error);
      // Optionally track the error
      appInsights.trackException({
        exception: error,
        severityLevel: 2,
        properties: {
          name: "WebsiteHits_Hover_Error",
          component: "handleBarHover",
        },
      });
    }
  };

  // Track empty or invalid data scenarios
  useEffect(() => {
    if (!data || data.length === 0) {
      appInsights.trackEvent({
        name: "WebsiteHits_No_Data",
        properties: {
          reason: "empty_data_array",
          component: "Website_Visitors_Chart",
        },
      });
    } else if (sum === 0) {
      appInsights.trackEvent({
        name: "WebsiteHits_Zero_Visits",
        properties: {
          dataPoints: data.length,
          weekDays: data.map(item => item.name),
          reason: "all_visits_zero",
        },
      });
    }
  }, [data, sum]);

  // Track weekly performance patterns
  useEffect(() => {
    if (data && data.length > 0 && sum > 0) {
      const validData = data.filter(item => !isNaN(item.value));
      if (validData.length > 0) {
        const peakDay = validData.reduce((max, day) => day.value > max.value ? day : max, validData[0]);
        const lowestDay = validData.reduce((min, day) => day.value < min.value ? day : min, validData[0]);
        
        appInsights.trackEvent({
          name: "WebsiteHits_Weekly_Pattern",
          properties: {
            totalWeeklyVisits: sum,
            peakDay: peakDay.name,
            peakDayVisits: peakDay.value,
            lowestDay: lowestDay.name,
            lowestDayVisits: lowestDay.value,
            peakToLowRatio: lowestDay.value > 0 ? (peakDay.value / lowestDay.value).toFixed(2) : 'infinite',
            averageDailyVisits: (sum / validData.length).toFixed(1),
            weekendTraffic: validData
              .filter(day => ['Sat', 'Sun'].includes(day.name))
              .reduce((sum, day) => sum + day.value, 0),
            weekdayTraffic: validData
              .filter(day => !['Sat', 'Sun'].includes(day.name))
              .reduce((sum, day) => sum + day.value, 0),
          },
        });
      }
    }
  }, [data, sum]);

  return (
    <div className="website-hits-card ">
      <div className="website-hits-header">
        {/* Title Section */}
        <div className="website-hits-title">
          <h1>Website Visitors</h1>
        </div>

        {/* Total Hits Section */}
        <div className="website-hits-total">
          <h1>Total Visits This Week</h1>
          <p className="total-hits-count">{sum}</p>
        </div>
      </div>

      {/* Chart Section */}
      <ResponsiveContainer height="80%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
          onClick={handleBarInteraction}
          onMouseEnter={handleBarHover}
        >
          {/* Define the gradient */}
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#383D8D" />
              <stop offset="100%" stopColor="#A42066" />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            label={{
              value: "Days of the Week",
              position: "bottom",
              offset: -4,
            }}
          />
          <YAxis dataKey="value" />

          {/* Apply the gradient here */}
          <Bar 
            dataKey="value" 
            fill="url(#barGradient)" 
            radius={[12, 12, 0, 0]}
            onMouseEnter={handleBarHover}
          >
            <LabelList dataKey="value" position="insideTop" fill="#FFFFFF" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default WebsiteHits;